/**
 * GQty: You can safely modify this file based on your needs.
 */

import {
  Cache,
  createClient,
  defaultResponseHandler,
  type QueryFetcher
} from 'gqty';
import { ResolveFn } from 'gqty/Client/resolvers';
import { createClient as createSubscriptionsClient } from 'graphql-ws';
import {
  generatedSchema,
  scalarsEnumsHash,
  type GeneratedSchema,
} from './schema.generated';

const rawSchema = generatedSchema as Record<string, Record<string, { __type: string }>>;
for (const [typeName, typeDef] of Object.entries(rawSchema)) {
  for (const [propName, propDef] of Object.entries(typeDef)) {
    if (propDef.__type === '[[Position]]') {
      rawSchema[typeName][propName].__type = '[Position]';
    }
  }
}

const queryFetcher: QueryFetcher = async function (
  { query, variables, operationName },
  fetchOptions
) {
  // Modify "/api/graphql/" if needed
  const response = await fetch('/api/graphql/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query,
      variables,
      operationName,
    }),
    mode: 'cors',
    ...fetchOptions,
  });

  return await defaultResponseHandler(response);
};

const subscriptionsClient =
  typeof window !== 'undefined'
    ? createSubscriptionsClient({
        lazy: true,
        url: () => {
          // Modify if needed
          const url = new URL(
            '/api/graphql/',
            window.location.href
          );
          url.protocol = url.protocol.replace('http', 'ws');
          return url.href;
        },
      })
    : undefined;

const cache = new Cache(
  undefined,
  /**
   * Default option is immediate cache expiry but keep it for 5 minutes,
   * allowing soft refetches in background.
   */
  {
    maxAge: 0,
    staleWhileRevalidate: 5 * 60 * 1000,
    normalization: true,
  }
);

export const client = createClient<GeneratedSchema>({
  schema: generatedSchema,
  scalars: scalarsEnumsHash,
  cache,
  fetchOptions: {
    fetcher: queryFetcher,
    subscriber: subscriptionsClient,
  },
});

// Core functions
export const { subscribe, schema } = client;

export const resolve: ResolveFn<GeneratedSchema> = (fn, options) => {
  const newQueryFetcher: QueryFetcher = async function (
    { query, variables, operationName },
    fetchOptions
  ) {
    // Modify "/api/graphql/" if needed
    const response = await fetch('/api/graphql/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables,
        operationName,
      }),
      mode: 'cors',
      ...fetchOptions,
    });

    return await defaultResponseHandler(response);
  };

  const newCache = new Cache(
    undefined,
    /**
     * Default option is immediate cache expiry but keep it for 5 minutes,
     * allowing soft refetches in background.
     */
    {
      maxAge: 0,
      staleWhileRevalidate: 5 * 60 * 1000,
      normalization: true,
    }
  );

  const newClient = createClient<GeneratedSchema>({
    schema: generatedSchema,
    scalars: scalarsEnumsHash,
    cache: newCache,
    fetchOptions: {
      fetcher: newQueryFetcher,
      subscriber: subscriptionsClient,
    },
  });
  return newClient.resolve(fn, options);
};

// Legacy functions
export const {
  query,
  mutation,
  mutate,
  subscription,
  resolved,
  refetch,
  track,
} = client;

export * from './schema.generated';
